"use client";

import React, { useRef } from "react";
import { UserCheck, Target, Eye, TrendingUp, ArrowRight, Award, CircleCheck as CheckCircle2, FileSearch, Sparkles } from 'lucide-react';
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const expertiseAreas = [{
  id: 1,
  name: "Technical SEO"
}, {
  id: 2,
  name: "On-Page SEO"
}, {
  id: 3,
  name: "Keyword Strategy"
}, {
  id: 4,
  name: "Content Strategy"
}, {
  id: 5,
  name: "Local SEO"
}, {
  id: 6,
  name: "National SEO"
}, {
  id: 7,
  name: "International SEO"
}, {
  id: 8,
  name: "B2B SEO & Lead Gen"
}, {
  id: 9,
  name: "Ecommerce SEO"
}, {
  id: 10,
  name: "SEO Audits"
}, {
  id: 11,
  name: "AI Search Optimisation"
}];
const valuePrinciples = [{
  id: 1,
  title: "Direct Access",
  description: "Work directly with the specialist leading your analysis and strategy.",
  iconName: "user"
}, {
  id: 2,
  title: "Strategic SEO",
  description: "Every recommendation is based on your business goals, search demand, competitors, and buyer intent.",
  iconName: "target"
}, {
  id: 3,
  title: "Complete Transparency",
  description: "Clear deliverables, plain-English explanations, and full visibility into every technical decision.",
  iconName: "eye"
}, {
  id: 4,
  title: "Focus on Results",
  description: "Prioritising pipeline impact, qualified enquiries, and durable revenue over vanity traffic metrics.",
  iconName: "trending"
}];
export default function Meet() {
  const containerRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = containerRef.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".gsap-reveal", {
        y: 35
      });
      gsap.to(".gsap-reveal", {
        opacity: 1,
        y: 0,
        duration: 0.7,
        stagger: 0.1,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set(".gsap-reveal", {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: containerRef
  });
  return <section id="meetyassineintro" data-nav="light" className="bg-background text-foreground relative py-20 lg:py-28 overflow-hidden">
      <div ref={containerRef} className="container mx-auto px-4 md:px-6 max-w-7xl">
        
        {/* Intro Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center mb-20">
          
          {/* Visual Column */}
          <div className="lg:col-span-5 gsap-reveal" style={{
          opacity: 0
        }}>
            <div className="relative mx-auto max-w-md lg:max-w-none">
              <div className="absolute -inset-3 rounded-2xl border-2 border-accent/40 translate-x-2 translate-y-2 pointer-events-none" />
              
              <div className="relative rounded-xl overflow-hidden bg-muted shadow-lg border border-border">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/Seo-Consultant-2.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/Seo-Consultant-2.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/Seo-Consultant-2.png-wvc-sizes" alt="Yassine Essaadi - SEO Consultant" loading="lazy" decoding="async" className="w-full h-auto object-cover aspect-[4/5] object-center" />
                
                <div className="absolute bottom-4 left-4 right-4 bg-background/95 backdrop-blur-md p-4 rounded-lg border border-border/80 shadow-md flex items-center gap-3.5">
                  <div className="bg-primary/10 text-primary p-2.5 rounded-md shrink-0">
                    <Award className="size-6" />
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Trusted SEO Expertise
                    </div>
                    <div className="text-sm font-bold font-serif text-foreground">
                      Optimising Organic Growth Since 2016
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Narrative Column */}
          <div className="lg:col-span-7 flex flex-col justify-center space-y-6">
            
            <div className="gsap-reveal" style={{
            opacity: 0
          }}>
              <Badge variant="secondary" className="mb-3 px-3 py-1 font-medium text-xs tracking-wide">
                <Sparkles className="size-3.5 text-accent mr-1" />
                FREELANCE SEO CONSULTANT UK
              </Badge>
              
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif leading-tight tracking-tight text-foreground">
                SEO Expertise.{" "}
                <span className="relative inline-block text-primary">
                  Strategic Clarity.
                  <span className="absolute bottom-1 left-0 w-full h-2.5 bg-accent/25 -z-10 rounded-sm" />
                </span>{" "}
                Business Growth.
              </h1>
            </div>

            <p className="text-lg text-foreground/90 font-medium leading-relaxed gsap-reveal" style={{
            opacity: 0
          }}>
              An independent, hands-on SEO consultancy grounded in technical depth, strategic clarity, and measurable commercial results.
            </p>

            <div className="space-y-4 text-muted-foreground text-base leading-relaxed gsap-reveal" style={{
            opacity: 0
          }}>
              <p>
                Hi, I&apos;m Yassine Essaadi. Since 2016, I&apos;ve guided SMEs, B2B enterprises, and high-growth brands across the UK and internationally through complex organic search challenges and major algorithm shifts.
              </p>
              
              <blockquote className="border-l-4 border-primary pl-4 py-1.5 my-4 italic text-foreground font-medium bg-muted/50 rounded-r-md">
                &ldquo;true search performance is built on technical precision, accurate search intent, and commercial alignment — not vanity rankings or algorithmic shortcuts.&rdquo;
              </blockquote>

              <p>
                My consultancy philosophy prioritises sustainable authority and measurable revenue impact. From comprehensive technical migrations and crawl budget audits to scalable keyword architectures and AI search strategies, every recommendation is engineered around real commercial priorities.
              </p>
            </div>

            {/* Core Competencies Badges */}
            <div className="pt-2 gsap-reveal" style={{
            opacity: 0
          }}>
              <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                Core SEO Expertise Areas
              </h2>
              <div className="flex flex-wrap gap-2">
                {expertiseAreas.map((item, i) => <span key={item.id} data-index={i} className="inline-flex items-center gap-1.5 bg-card border border-border px-3 py-1.5 rounded-md text-xs font-medium text-foreground shadow-2xs hover:border-primary/50 transition-colors">
                    <CheckCircle2 className="size-3.5 text-primary" />
                    {item.name}
                  </span>)}
              </div>
            </div>

          </div>
        </div>

        {/* Consulting Philosophy & Principles */}
        <div className="mb-20">
          <div className="text-center max-w-2xl mx-auto mb-12 gsap-reveal" style={{
          opacity: 0
        }}>
            <h3 className="text-2xl md:text-3xl font-bold font-serif mb-3 text-foreground">
              Core Consulting Philosophy &amp; Approach
            </h3>
            <p className="text-muted-foreground text-sm md:text-base">
              A disciplined, data-driven methodology built on technical precision, clear communication, and sustainable commercial outcomes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {valuePrinciples.map((principle, i) => {
            let Icon = UserCheck;
            if (principle.iconName === "target") Icon = Target;
            if (principle.iconName === "eye") Icon = Eye;
            if (principle.iconName === "trending") Icon = TrendingUp;
            return <Card key={principle.id} data-index={i} className="gsap-reveal bg-card border-border hover:border-primary/40 hover:shadow-md transition-all duration-300 rounded-xl" style={{
              opacity: 0
            }}>
                  <CardContent className="p-6 flex flex-col h-full">
                    <div className="bg-muted text-primary w-12 h-12 rounded-lg flex items-center justify-center mb-5 shrink-0 border border-border">
                      <Icon className="size-6" />
                    </div>
                    <h4 className="text-lg font-bold font-serif mb-2 text-card-foreground">
                      {principle.title}
                    </h4>
                    <p className="text-sm text-muted-foreground leading-relaxed flex-grow">
                      {principle.description}
                    </p>
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>

        {/* Mini CTA Band */}
        <div className="gsap-reveal bg-muted/60 border border-border rounded-2xl p-8 md:p-12 text-center max-w-4xl mx-auto relative overflow-hidden shadow-sm" style={{
        opacity: 0
      }}>
          <div className="absolute top-0 right-0 w-64 h-64 bg-accent/10 rounded-full blur-3xl pointer-events-none -mr-20 -mt-20" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-primary/10 rounded-full blur-3xl pointer-events-none -ml-20 -mb-20" />

          <div className="relative z-10 space-y-6">
            <h3 className="text-2xl md:text-3xl font-bold font-serif text-foreground max-w-xl mx-auto">
              Want to know what&apos;s holding your website back?
            </h3>
            
            <p className="text-muted-foreground text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
              Let&apos;s review your current organic performance, identify untapped search opportunities, and determine the optimal roadmap to scale your organic pipeline.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
              <Button size="lg" className="w-full sm:w-auto font-medium" asChild>
                <Link to="/ContactPage">
                  Book a Free SEO Consultation
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>

              <Button variant="outline" size="lg" className="w-full sm:w-auto font-medium" asChild>
                <Link to="/SeoAuditsPage">
                  <FileSearch className="size-4 mr-1.5" />
                  Get a Free SEO Audit
                </Link>
              </Button>
            </div>
          </div>
        </div>

      </div>
    </section>;
}